import {
  Component,
  createContext,
  type FC,
  type ReactNode,
  Suspense,
  use,
  useState,
} from "react";
import { Eye, EyeOff } from "lucide-react";
import { Toaster } from "@renewmap/ui/toaster";
import { toast } from "@renewmap/ui/hooks/use-toast";
import { ToolsHeader } from "./tools-header";
import { usePromiseMemo } from "./promise-cache";

/**
 * Show a destructive toast for a non-fatal failure. The Toaster is
 * mounted by ToolsAppWrapper. Pass the caught error (or a detail string)
 * as the second argument to show it under the message.
 */
export const toastError = (message: string, error?: unknown) => {
  const description =
    error instanceof Error ? error.message : error ? String(error) : undefined;
  toast({ variant: "destructive", title: message, description });
};

class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="mx-auto mt-8 max-w-md text-center text-red-600">
          <h2 className="text-lg font-semibold">Something went wrong</h2>
          <p className="mt-2">{this.state.error.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const PeekButton: FC<{ peeking: boolean; onToggle: () => void }> = ({
  peeking,
  onToggle,
}) => {
  const label = peeking ? "Show tool content" : "Peek backdrop";
  return (
    <button
      onClick={onToggle}
      aria-label={label}
      title={label}
      className="ml-auto flex h-6 w-6 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-900"
    >
      {peeking ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );
};

export const ToolsAppWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const [peeking, setPeeking] = useState(false);

  return (
    <div className="min-h-[100vh] px-8 py-6">
      <ToolsHeader>
        <PeekButton peeking={peeking} onToggle={() => setPeeking((p) => !p)} />
      </ToolsHeader>
      <div hidden={peeking}>
        <ErrorBoundary>{children}</ErrorBoundary>
      </div>
      <Toaster />
    </div>
  );
};

const ToolDataContext = createContext<unknown>(null);

interface ToolDataProps<T> {
  load: () => Promise<T>;
  cacheKey: string;
  children: ReactNode;
}

const ToolDataProvider = <T,>({
  load,
  cacheKey,
  children,
}: ToolDataProps<T>) => {
  const data = use(usePromiseMemo(load, cacheKey));
  return (
    <ToolDataContext.Provider value={data}>{children}</ToolDataContext.Provider>
  );
};

/**
 * Optional bootstrap-data primitive for tools that load everything up
 * front. Suspends until `load` resolves (cached by `cacheKey`), then
 * provides the result via useToolData. Load errors throw to the nearest
 * error boundary (ToolsAppWrapper's).
 */
export const ToolData = <T,>(props: ToolDataProps<T>) => (
  <Suspense fallback={<p className="text-center text-sm">Loading…</p>}>
    <ToolDataProvider {...props} />
  </Suspense>
);

/**
 * Returns the bootstrap data loaded by the enclosing ToolData. `T` must
 * match the return type of that ToolData's `load` — each tool page
 * mounts exactly one ToolData, so there is only one candidate.
 */
export const useToolData = <T,>(): T => {
  const data = use(ToolDataContext);
  if (data == null) {
    throw new Error("useToolData must be used inside ToolData");
  }
  return data as T;
};
